import { FC, HTMLAttributes, ReactNode, SyntheticEvent, KeyboardEvent, useCallback, useId } from 'react';
import { css, Theme } from '@emotion/react';
import Autocomplete, { AutocompleteChangeReason, AutocompleteCloseReason, AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import type { InputProps } from '@mui/material/Input';
import Popover, { PopoverProps } from '@mui/material/Popover';
import type { PopperProps } from '@mui/material/Popper';
import { borderRadius } from 'client/Theme';
import { SearchableDropdownInput } from './SearchableDropdown/SearchableDropdownInput';

const popoverArrowURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg width='24' height='8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath ` +
  `fill-rule='evenodd' clip-rule='evenodd' d='M12.95.69A12 12 0 0 0 24 8H0A12 12 0 0 0 11.05.69a1 1 0 0 1 1.9 0Z' ` +
  `fill='${theme.color.background.replace('#', '%23')}'/%3E%3C/svg%3E")`;

const popoverStyle = (theme: Theme) => css`
  > .MuiPaper-root {
    margin-top: 10px;
    overflow: visible;
    box-shadow: rgba(17, 17, 26, 0.1) 0px 4px 16px, rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px;
    border-radius: ${borderRadius}px;
    background-color: ${theme.color.background};

    ::before {
      // The cool triangle that points up
      content: '';
      display: block;
      position: absolute;
      top: -10px;
      right: 16px;
      width: 30px;
      height: 16px;
      background-image: ${popoverArrowURI(theme)};
      background-size: 100%;
      background-repeat: no-repeat;
      filter: drop-shadow(0px -2px 4px rgba(17, 17, 26, 0.1));
    }

    ::after {
      // Coverup for the triangles shadow bleed
      content: '';
      display: block;
      position: absolute;
      top: 0px;
      right: 16px;
      width: 30px;
      height: 8px;
      background: ${theme.color.background};
    }
  }
`;

// FIXME: Input style
const autocompleteStyle = (theme: Theme) => css`
  padding: 8px;
`;

const popperStyle = (theme: Theme) => css`
  margin-left: 8px;
  margin-bottom: 8px;
  padding-right: 8px;

  > .MuiPaper-root {
    background-color: ${theme.color.background};
    box-shadow: none;

    > ul {
      padding: 0;

      > li {
        min-width: 280px;
      }
    }
  }
`;

const PopperComponent: FC<PopperProps> = ({ disablePortal, anchorEl, open, ...other }) => (
  <div {...(other as any)} style={{ width: 'fit-content' }} css={popperStyle} />
);

export type SearchableDropdownRenderOptionFunction<Option extends { id: string }> = (
  props: HTMLAttributes<HTMLLIElement>,
  option: Option,
  state: AutocompleteRenderOptionState
) => ReactNode;

export interface SearchableDropdownProps<Option extends { id: string }> {
  anchorEl: HTMLElement | null;
  value: string | null;
  options: Option[];
  getOptionName?: (option: Option) => string;
  renderOption: SearchableDropdownRenderOptionFunction<Option>;
  popoverProps?: Partial<PopoverProps>;
  inputProps?: Partial<InputProps>;
  onClose: () => void;
  onChange: (option: Option | null) => void;
}

export const SearchableDropdown = <Option extends { id: string }>({
  anchorEl,
  value,
  options = [],
  getOptionName,
  renderOption,
  popoverProps,
  inputProps = {},
  onClose,
  onChange,
}: SearchableDropdownProps<Option>) => {
  const id = useId();

  const handleClose = useCallback(
    (_: unknown, reason: AutocompleteCloseReason) => {
      if (reason === 'escape') onClose();
    },
    [onClose]
  );

  const handleChange = useCallback(
    (event: SyntheticEvent<Element, Event>, option: Option | null, reason: AutocompleteChangeReason) => {
      if (event.type === 'keydown' && (event as KeyboardEvent).key === 'Backspace' && reason === 'removeOption') {
        return;
      }
      onChange(option);
      onClose();
    },
    [onChange]
  );

  return (
    <Popover
      id={id}
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      {...popoverProps}
      css={popoverStyle}
    >
      <Autocomplete
        open
        onClose={handleClose}
        onChange={handleChange}
        disableCloseOnSelect
        PopperComponent={PopperComponent}
        renderTags={() => null}
        noOptionsText="No options"
        renderOption={renderOption}
        options={options}
        getOptionLabel={getOptionName || ((option) => option.id)}
        renderInput={(props) => <SearchableDropdownInput {...props} InputProps={{ ...inputProps, ref: props.InputProps.ref }} />}
        css={autocompleteStyle}
      />
    </Popover>
  );
};
